/**
 * Legal links (privacy and terms) — **store requirement**, not decoration.
 *
 * Google Play and the Microsoft Store require the privacy policy to be
 * accessible **from inside the app**, not just from the store listing. Non-compliance
 * is silent: nobody warns you that the link is dead, the
 * app simply does not comply.
 *
 * And dead it was in the native binary. On the web, `<a href="/privacy.html"
 * target="_blank">` opens a tab; inside Tauri's WebView **nothing happens**
 * — there is no new window manager, so the click is swallowed without error
 * or navigation. The same `<a>` that works in the PWA is an inert button in the
 * APK.
 *
 * Solution (the same already used by social login for OAuth): in native we
 * intercept the click and open the PUBLIC URL in the system browser with
 * `@tauri-apps/plugin-opener`. The public URL is needed because inside the
 * binary the origin is `tauri://localhost`, which the system browser cannot
 * resolve — hence `WEB_URL` in the runtime config.
 */

import { isTauriRuntime } from "@/lib/platform";
import { WEB_URL } from "@/lib/runtime-config";

/** Legal documents published as static in `apps/web/public/`. */
export const LEGAL_DOCUMENTS = {
  privacy: "/privacy.html",
  terms: "/terms.html",
} as const;

export type LegalDocument = keyof typeof LEGAL_DOCUMENTS;

/**
 * Document URL. Relative on web (same origin, works in any
 * environment) and ABSOLUTE on top of `WEB_URL` in native, where the origin is
 * `tauri://localhost` and only a real public URL works.
 */
export function legalDocumentUrl(doc: LegalDocument): string {
  const path = LEGAL_DOCUMENTS[doc];
  return isTauriRuntime() ? `${WEB_URL.replace(/\/+$/, "")}${path}` : path;
}

/**
 * Opens the document. On web lets the normal `<a>` navigation pass through (returns
 * `false`); in native intercepts it and returns `true` so the caller calls
 * `preventDefault()`.
 */
export function openLegalDocument(doc: LegalDocument): boolean {
  if (!isTauriRuntime()) {
    return false;
  }
  void (async () => {
    // Dynamic import: keeps the Tauri plugin outside the web bundle.
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(legalDocumentUrl(doc));
  })();
  return true;
}

/** Handler ready for an `<a>`: intercepts only when necessary. */
export function handleLegalLinkClick(event: MouseEvent, doc: LegalDocument): void {
  if (openLegalDocument(doc)) {
    event.preventDefault();
  }
}
